import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from '../../api/axios';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import './PaymentSuccess.css';

function PaymentSuccess({ currentUser, setCurrentUser }) {
  const navigate = useNavigate();
  const { refreshWorkspaceContext } = useWorkspace();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('verifying'); // verifying, success, error

  useEffect(() => {
    const processedKey = `processed_payment_${sessionId}`;

    if (sessionStorage.getItem(processedKey)) {
      setStatus('success');

      const timer = setTimeout(() => {
        navigate('/');
      }, 3000); // Shorter redirect for already processed
      return () => clearTimeout(timer);
    }

    const verifyAndRefresh = async () => {
      let tier = null;

      // Step 1: Verify with Stripe and get the tier from session metadata
      const maxRetries = 3;
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          const verifyRes = await axios.get(`stripe/verify-session?session_id=${sessionId}`);

          if (verifyRes.data?.success && verifyRes.data?.tier) {
            tier = verifyRes.data.tier;
            break;
          } else {
            console.warn(`[PaymentSuccess] Attempt ${attempt}:`, verifyRes.data);
          }
        } catch (error) {
          console.error(
            `[PaymentSuccess] Attempt ${attempt} failed:`,
            error.response?.data || error.message
          );
        }

        if (attempt < maxRetries) {
          await new Promise((resolve) => setTimeout(resolve, attempt * 2000));
        }
      }

      // Step 2: If we got the tier from Stripe, also call profile/tier directly as backup
      if (tier) {
        try {
          await axios.post('profile/tier', { tier });
        } catch (tierErr) {
          // This might fail if already updated — that's OK
          console.warn(
            '[PaymentSuccess] profile/tier call:',
            tierErr.response?.data?.message || tierErr.message
          );
        }
      }

      // Step 3: Refresh profile to update UI
      await new Promise((resolve) => setTimeout(resolve, 500));
      try {
        const response = await axios.get('profile');
        if (response.data?.success) {
          const p = response.data.data;
          setCurrentUser((prev) => ({
            ...prev,
            tier: p.tier || 'Free',
            creditBalance: p.credit_balance || 0,
            creditLimit: p.credit_limit || 0,
          }));
          localStorage.setItem('userTier', p.tier || 'Free');
          localStorage.setItem('creditBalance', String(p.credit_balance || 0));
          sessionStorage.setItem(processedKey, 'true');
          setStatus('success');

          setTimeout(() => {
            if (refreshWorkspaceContext) refreshWorkspaceContext();
            window.location.href = '/';
          }, 3000);
        }
      } catch (profileErr) {
        console.error('[PaymentSuccess] Failed to refresh profile:', profileErr);
        setStatus('error');
        setTimeout(() => navigate('/'), 6000);
      }
    };

    if (sessionId && !sessionStorage.getItem(processedKey)) {
      verifyAndRefresh();
    } else if (!sessionId) {
      setStatus('error');
      setTimeout(() => navigate('/'), 6000);
    }
  }, [navigate, sessionId, setCurrentUser, refreshWorkspaceContext]);

  return (
    <div className="payment-success-container">
      <div className="success-card">
        <div className="success-icon">
          <i
            className={`fas ${status === 'verifying' ? 'fa-spinner fa-spin' : status === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`}
          ></i>
        </div>
        <h1>
          {status === 'verifying'
            ? 'Processing Payment...'
            : status === 'success'
              ? 'Payment Successful!'
              : 'Payment Status'}
        </h1>
        <p>
          {status === 'verifying'
            ? 'Verifying your payment and updating your plan. Please wait...'
            : status === 'success'
              ? 'Your account has been updated with your new tier and credits.'
              : 'There was an issue updating your plan. Please refresh the page or contact support.'}
        </p>

        <div className="next-steps">
          <button className="btn-primary" onClick={() => (window.location.href = '/')}>
            Go to Dashboard
          </button>
          <p className="redirect-hint">Redirecting to dashboard shortly...</p>
        </div>
      </div>
    </div>
  );
}

export default PaymentSuccess;
